import { Controller } from "@hotwired/stimulus";
import { db } from "../db/index.js";
import { DebugLogger } from "../db/debugLogger.js";
import { activeRouteFrom } from "../db/sanitationRoutes.js";
import { getFarmIdFromUrl } from "../utils.js";

const LAUNCHER_VISIBLE_STATUSES = new Set(["in_progress", "completed"]);
const VALID_ROUTE_STEPS = new Set([
  "route",
  "tree",
  "tree_sample",
  "open_ended_checkpoint",
  "samples_checkpoint",
]);

export async function collectRouteDebugState(database = db) {
  if (!database?.sanitationRoutes) {
    throw new Error("IndexedDB connection not available");
  }

  const [routes, datapoints, locations, fileQueue] = await Promise.all([
    database.sanitationRoutes.toArray(),
    database.routeDatapoints.toArray(),
    database.routeLocations.toArray(),
    database.fileQueue.toArray(),
  ]);

  return {
    collectedAt: new Date().toISOString(),
    page: window.location.href,
    origin: window.location.origin,
    summary: summarizeRouteState({
      routes,
      datapoints,
      locations,
      fileQueue,
    }),
    raw: {
      sanitationRoutes: routes,
      routeDatapoints: datapoints,
      routeLocations: locations,
      fileQueue,
    },
  };
}

export function summarizeRouteState({
  routes,
  datapoints,
  locations,
  fileQueue,
}) {
  const currentFarmId = getFarmIdFromUrl(window.location.pathname);
  const activeRouteId = activeRouteFrom(routes, currentFarmId)?.id;
  const datapointCounts = countBy(datapoints, "sanitation_route_id");
  const locationCounts = countBy(locations, "sanitation_route_id");
  const fileCounts = countBy(fileQueue, "sanitation_route_id");

  return {
    routeCount: routes.length,
    datapointCount: datapoints.length,
    locationCount: locations.length,
    fileQueueCount: fileQueue.length,
    routes: routes
      .map((route) => ({
        id: route.id,
        farmId: route.farm_id,
        status: route.status || "(missing)",
        active: sameId(route.id, activeRouteId),
        form: route.sanitation_form_name || route.sanitation_form_id,
        lot: route.lot_name || route.lot_id,
        monitor: route.sanitation_monitor_name || route.sanitation_monitor_id,
        currentQuestionType: route.current_question_type || "(none)",
        currentTreeNumber: route.current_tree_number,
        totalTreesInRoute: route.total_trees_in_route,
        createdAt: route.created_at,
        completedAt: route.completed_at,
        datapointCount: datapointCounts.get(route.id) || 0,
        locationCount: locationCounts.get(route.id) || 0,
        fileQueueCount: fileCounts.get(route.id) || 0,
        visibleInLauncher:
          sameId(route.farm_id, currentFarmId) &&
          LAUNCHER_VISIBLE_STATUSES.has(route.status),
      }))
      .sort((a, b) =>
        String(b.createdAt || "").localeCompare(String(a.createdAt || "")),
      ),
  };
}

export function buildRouteRecoveryAttributes(route, routeDatapoints = []) {
  const latestDatapoint = latestRouteDatapoint(routeDatapoints, route.id);
  const nextQuestionType = recoverQuestionType(route, latestDatapoint);

  return {
    status: "in_progress",
    last_active_at: new Date().toISOString(),
    completed_at: null,
    current_question_type: nextQuestionType,
    current_tree_number:
      route.current_tree_number || latestDatapoint?.tree_number || 1,
    current_section_number: route.current_section_number || 1,
    current_sample_number: route.current_sample_number || 1,
  };
}

function latestRouteDatapoint(routeDatapoints, routeId) {
  return routeDatapoints
    .filter((datapoint) => sameId(datapoint.sanitation_route_id, routeId))
    .sort((a, b) => {
      const createdComparison = String(b.created_at || "").localeCompare(
        String(a.created_at || ""),
      );
      if (createdComparison !== 0) return createdComparison;

      return Number(b.id || 0) - Number(a.id || 0);
    })[0];
}

function recoverQuestionType(route, latestDatapoint) {
  if (VALID_ROUTE_STEPS.has(route.current_question_type)) {
    return route.current_question_type;
  }

  if (VALID_ROUTE_STEPS.has(latestDatapoint?.question_type)) {
    return latestDatapoint.question_type;
  }

  if (route.tree_question_id) return "tree";
  if (route.tree_sample_question_id) return "tree_sample";
  if (route.route_question_id) return "route";

  return "tree";
}

function countBy(records, key) {
  return records.reduce((counts, record) => {
    const value = record[key];
    if (value === undefined || value === null) return counts;

    counts.set(value, (counts.get(value) || 0) + 1);
    return counts;
  }, new Map());
}

function sameId(left, right) {
  if (
    left === undefined ||
    left === null ||
    right === undefined ||
    right === null
  ) {
    return false;
  }

  return String(left) === String(right);
}

// Connects to data-controller="debug-panel"
export default class extends Controller {
  static targets = ["panel", "logs", "storage", "toggle", "routeState"];

  connect() {
    // Hide panel by default
    this.hidePanel();

    // Listen for debug key combination (tap 5 times quickly)
    this.tapCount = 0;
    this.tapTimeout = null;

    if (this.hasToggleTarget) {
      this.toggleTarget.addEventListener(
        "click",
        this.handleToggleTap.bind(this),
      );
    }
  }

  handleToggleTap() {
    this.tapCount++;

    if (this.tapTimeout) {
      clearTimeout(this.tapTimeout);
    }

    this.tapTimeout = setTimeout(() => {
      this.tapCount = 0;
    }, 2000); // Reset after 2 seconds

    if (this.tapCount >= 5) {
      this.tapCount = 0;
      this.togglePanel();
    }
  }

  togglePanel() {
    if (this.panelTarget.classList.contains("hidden")) {
      this.showPanel();
    } else {
      this.hidePanel();
    }
  }

  showPanel() {
    this.panelTarget.classList.remove("hidden");
    this.refreshLogs();
    this.refreshStorage();
    this.refreshRouteState();
  }

  hidePanel() {
    this.panelTarget.classList.add("hidden");
  }

  refreshLogs() {
    if (!this.hasLogsTarget) return;

    const logs = DebugLogger.getLogs();
    const recentLogs = logs.slice(0, 20); // Show only recent 20 logs

    this.logsTarget.innerHTML = recentLogs
      .map((log) => {
        const time = new Date(log.timestamp).toLocaleTimeString();
        const levelColor = this.getLevelColor(log.level);
        const contextStr =
          Object.keys(log.context).length > 0
            ? JSON.stringify(log.context, null, 2)
            : "";

        return `
        <div class="p-2 mb-2 text-xs border rounded ${levelColor}">
          <div class="font-bold">[${time}] ${log.level}: ${log.message}</div>
          ${
            contextStr
              ? `<pre class="mt-1 text-xs opacity-75">${contextStr}</pre>`
              : ""
          }
        </div>
      `;
      })
      .join("");

    if (recentLogs.length === 0) {
      this.logsTarget.innerHTML =
        '<div class="p-2 text-xs text-gray-500">No debug logs</div>';
    }
  }

  async refreshStorage() {
    if (!this.hasStorageTarget) return;

    const storageInfo = await DebugLogger.getStorageInfo();

    if (storageInfo) {
      this.storageTarget.innerHTML = `
        <div class="p-2 text-xs">
          <div><strong>Storage Usage:</strong> ${
            storageInfo.usagePercent
          }%</div>
          <div><strong>Used:</strong> ${Math.round(
            storageInfo.usage / 1024 / 1024,
          )}MB</div>
          <div><strong>Available:</strong> ${Math.round(
            storageInfo.available / 1024 / 1024,
          )}MB</div>
          <div><strong>Total:</strong> ${Math.round(
            storageInfo.quota / 1024 / 1024,
          )}MB</div>
        </div>
      `;
    } else {
      this.storageTarget.innerHTML =
        '<div class="p-2 text-xs text-gray-500">Storage info unavailable</div>';
    }
  }

  async refreshRouteState() {
    if (!this.hasRouteStateTarget) return;

    this.routeStateTarget.textContent = "Loading local route state...";

    try {
      this.lastRouteState = await collectRouteDebugState();
      this.renderRouteState(this.lastRouteState.summary);
    } catch (error) {
      this.routeStateTarget.textContent = `Unable to read route state: ${error.message}`;
    }
  }

  renderRouteState(summary) {
    this.routeStateTarget.replaceChildren();

    const wrapper = document.createElement("div");
    wrapper.className = "p-2 space-y-2 text-xs";

    wrapper.appendChild(
      this.summaryLine(
        `${summary.routeCount} routes, ${summary.datapointCount} datapoints, ${summary.locationCount} locations, ${summary.fileQueueCount} files`,
      ),
    );

    if (summary.routes.length === 0) {
      wrapper.appendChild(this.mutedText("No local sanitation routes found."));
      this.routeStateTarget.appendChild(wrapper);
      return;
    }

    summary.routes.forEach((route) => {
      wrapper.appendChild(this.routeCard(route));
    });

    this.routeStateTarget.appendChild(wrapper);
  }

  routeCard(route) {
    const card = document.createElement("div");
    card.className = "p-2 space-y-1 rounded border border-base-300 bg-base-100";

    const title = document.createElement("div");
    title.className = "flex gap-2 justify-between items-center font-semibold";
    title.textContent = `#${route.id} ${route.status}${
      route.active ? " active" : ""
    }`;

    if (!route.visibleInLauncher) {
      const recoverButton = document.createElement("button");
      recoverButton.type = "button";
      recoverButton.className = "btn btn-xs btn-warning";
      recoverButton.dataset.routeId = route.id;
      recoverButton.dataset.action = "click->debug-panel#recoverRoute";
      recoverButton.textContent = "Recover";
      title.appendChild(recoverButton);
    }

    card.appendChild(title);

    [
      ["Farm", route.farmId],
      ["Form", route.form],
      ["Lot", route.lot],
      ["Monitor", route.monitor],
      ["Step", route.currentQuestionType],
      [
        "Tree",
        `${route.currentTreeNumber ?? "?"} / ${route.totalTreesInRoute ?? "?"}`,
      ],
      [
        "Counts",
        `${route.datapointCount} datapoints, ${route.locationCount} locations, ${route.fileQueueCount} files`,
      ],
      ["Created", route.createdAt],
      ["Completed", route.completedAt || "(not completed)"],
      ["Launcher", route.visibleInLauncher ? "visible" : "hidden"],
    ].forEach(([label, value]) => {
      card.appendChild(this.keyValueLine(label, value));
    });

    return card;
  }

  summaryLine(text) {
    const element = document.createElement("div");
    element.className = "font-medium";
    element.textContent = text;
    return element;
  }

  mutedText(text) {
    const element = document.createElement("div");
    element.className = "text-base-content/60";
    element.textContent = text;
    return element;
  }

  keyValueLine(label, value) {
    const line = document.createElement("div");
    line.className = "break-words text-base-content/80";

    const key = document.createElement("span");
    key.className = "font-medium";
    key.textContent = `${label}: `;

    const val = document.createElement("span");
    val.textContent =
      value === undefined || value === null ? "(blank)" : String(value);

    line.append(key, val);
    return line;
  }

  getLevelColor(level) {
    switch (level) {
      case "ERROR":
        return "bg-red-100 border-red-300";
      case "WARN":
        return "bg-yellow-100 border-yellow-300";
      case "INFO":
        return "bg-blue-100 border-blue-300";
      default:
        return "bg-gray-100 border-gray-300";
    }
  }

  clearLogs() {
    DebugLogger.clearLogs();
    this.refreshLogs();
  }

  async exportLogs() {
    const exportData = await DebugLogger.exportLogs();

    this.downloadJson(
      exportData,
      `artemis-debug-${new Date().toISOString().split("T")[0]}.json`,
    );
  }

  async exportRouteState() {
    const exportData = await this.routeStateExportData();

    this.downloadJson(
      exportData,
      `artemis-routes-${new Date().toISOString().split("T")[0]}.json`,
    );
  }

  async copyLogs() {
    const exportData = await DebugLogger.exportLogs();

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(exportData)
        .then(() => {
          alert("Debug logs copied to clipboard");
        })
        .catch(() => {
          this.fallbackCopy(exportData);
        });
    } else {
      this.fallbackCopy(exportData);
    }
  }

  async copyRouteState() {
    const exportData = await this.routeStateExportData();
    await this.copyText(exportData, "Local route state copied to clipboard");
  }

  async recoverRoute(event) {
    const routeId = Number(event.currentTarget.dataset.routeId);
    if (!routeId) return;

    if (
      !confirm(
        "Recover this local route? It will become active and visible in the route launcher. No server data will be changed.",
      )
    ) {
      return;
    }

    try {
      const route = await db.sanitationRoutes.get(routeId);
      if (!route) throw new Error("Route not found in local storage.");

      const currentFarmId = getFarmIdFromUrl(window.location.pathname);
      if (currentFarmId && !sameId(route.farm_id, currentFarmId)) {
        throw new Error(
          `Route belongs to farm ${route.farm_id}, but this page is farm ${currentFarmId}.`,
        );
      }

      const routeDatapoints = await db.routeDatapoints
        .where("sanitation_route_id")
        .equals(routeId)
        .toArray();
      const recoveryAttributes = buildRouteRecoveryAttributes(
        route,
        routeDatapoints,
      );

      // The recovery attributes stamp a fresh last_active_at, so the recovered
      // route becomes the active route without touching any other route.
      await db.sanitationRoutes.update(routeId, recoveryAttributes);

      alert("Local route recovered. The route launcher will reload.");
      window.location.reload();
    } catch (error) {
      alert(`Unable to recover route: ${error.message}`);
    }
  }

  async routeStateExportData() {
    if (!this.lastRouteState) {
      this.lastRouteState = await collectRouteDebugState();
    }

    return JSON.stringify(this.lastRouteState, null, 2);
  }

  downloadJson(text, filename) {
    const blob = new Blob([text], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async copyText(text, successMessage) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        alert(successMessage);
        return;
      } catch {
        this.fallbackCopy(text, successMessage);
        return;
      }
    }

    this.fallbackCopy(text, successMessage);
  }

  fallbackCopy(text, successMessage = "Debug logs copied to clipboard") {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
    alert(successMessage);
  }
}
